// src/pages/GradesPage/components/AssessmentGradingModal.jsx
import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faSave,
	faGraduationCap,
	faClipboardList,
	faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { Modal } from "../../../components/common";
import { Loading } from "../../../components/common";
import styles from "./AssessmentGradingModal.module.css";

const AssessmentGradingModal = ({
	isOpen,
	onClose,
	studentId,
	studentName,
	subjectId,
	subjectName,
	month,
	departmentId,
	assessments = [],
	studentGrades = {},
	onSaveGrades,
	isLoading = false,
	isSaving = false,
}) => {
	const [grades, setGrades] = useState({});

	// Initialize grades when modal opens and when assessments change
	useEffect(() => {
		if (isOpen && assessments.length > 0) {
			const initialGrades = {};
			assessments.forEach((assessment) => {
				const existingGrade = studentGrades[assessment.id];
				initialGrades[assessment.id] = existingGrade?.grade || "";
			});
			setGrades(initialGrades);
		} else if (isOpen && assessments.length === 0) {
			// FIX: Clear grades when no assessments (prevents showing previous totals)
			setGrades({});
		}
	}, [isOpen, assessments, studentGrades]);

	const handleGradeChange = (assessmentId, value) => {
		// Ensure the value is within bounds
		const numValue = parseFloat(value);
		const assessment = assessments.find((a) => a.id === assessmentId);
		const maxPoints = parseFloat(assessment?.points || 0);

		if (value === "" || (numValue >= 0 && numValue <= maxPoints)) {
			setGrades((prev) => ({
				...prev,
				[assessmentId]: value,
			}));
		}
	};

	const handleSave = () => {
		const gradesToSave = Object.entries(grades).map(
			([assessmentId, points]) => ({
				assessmentId,
				studentId,
				points: points === "" ? null : parseFloat(points),
			})
		);
		onSaveGrades(gradesToSave);
	};

	const getTotalPoints = () => {
		// FIX: Only calculate total from current assessments
		if (assessments.length === 0) return 0;

		return Object.entries(grades).reduce((total, [assessmentId, grade]) => {
			// Only count grades for current assessments
			const isCurrentAssessment = assessments.some(
				(a) => a.id === assessmentId
			);
			if (!isCurrentAssessment) return total;

			const numValue = parseFloat(grade);
			return total + (isNaN(numValue) ? 0 : numValue);
		}, 0);
	};

	const getTotalMaxPoints = () => {
		// FIX: Only calculate total from current assessments
		return assessments.reduce((total, assessment) => {
			return total + parseFloat(assessment.points || 0);
		}, 0);
	};

	const footer = (
		<>
			<button
				type="button"
				onClick={onClose}
				disabled={isSaving}
				className="btn btn-secondary"
			>
				Cancel
			</button>
			<button
				type="button"
				onClick={handleSave}
				disabled={isSaving}
				className="btn btn-primary"
			>
				{isSaving ? (
					<>
						<FontAwesomeIcon icon={faSpinner} spin />
						Saving...
					</>
				) : (
					<>
						<FontAwesomeIcon icon={faSave} />
						Save Grades
					</>
				)}
			</button>
		</>
	);

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Grade Assessments"
			size="large"
			footer={footer}
			loading={isSaving}
		>
			<div className={styles.modalContent}>
				{/* Student and Subject Info */}
				<div className={styles.headerInfo}>
					<div className={styles.studentInfo}>
						<FontAwesomeIcon icon={faGraduationCap} className={styles.icon} />
						<div>
							<h3 className={styles.studentName}>{studentName}</h3>
							<p className={styles.subjectName}>
								{subjectName} - {month}
							</p>
						</div>
					</div>
					<div className={styles.totalInfo}>
						<div className={styles.totalScore}>
							{getTotalPoints().toFixed(1)} / {getTotalMaxPoints()} points
						</div>
					</div>
				</div>

				{isLoading ? (
					<div className={styles.loadingContainer}>
						<Loading message="Loading assessments..." />
					</div>
				) : assessments.length === 0 ? (
					<div className={styles.emptyState}>
						<FontAwesomeIcon
							icon={faClipboardList}
							className={styles.emptyIcon}
						/>
						<h3>No Assessments Found</h3>
						<p>
							No assessments found for {month}. Create assessments first to
							grade students.
						</p>
					</div>
				) : (
					<div className={styles.assessmentsList}>
						{assessments.map((assessment) => (
							<div key={assessment.id} className={styles.assessmentItem}>
								<div className={styles.assessmentInfo}>
									<h4 className={styles.assessmentTitle}>{assessment.title}</h4>
									<div className={styles.assessmentMeta}>
										<span className={styles.assessmentType}>
											{assessment.type}
										</span>
										<span className={styles.assessmentDate}>
											{new Date(assessment.date).toLocaleDateString()}
										</span>
									</div>
								</div>
								<div className={styles.gradeInput}>
									<input
										type="number"
										min="0"
										max={assessment.points}
										step="0.1"
										placeholder="0"
										value={grades[assessment.id] || ""}
										onChange={(e) =>
											handleGradeChange(assessment.id, e.target.value)
										}
										className={styles.gradeField}
									/>
									<span className={styles.maxPoints}>
										/ {assessment.points}
									</span>
								</div>
							</div>
						))}
					</div>
				)}
			</div>
		</Modal>
	);
};

export default AssessmentGradingModal;
